import React from "react";
import { ArrowRight, Moon, Flame } from "lucide-react";
import { Overline } from "./WinterSections";
import { BOOKING_LINE } from "./winterContent";

function SolsticeCountdown() {
  const start = new Date(2026, 11, 18);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((start - today) / 86400000);
  if (days < 0) return null;
  const text = days === 0 ? "Today we gather by the fire" : days === 1 ? "Just 1 day until we gather by the fire" : `${days} days until we gather by the fire`;
  return (
    <p className="mt-6 text-center font-serif italic text-aviana-plum text-xl md:text-2xl flex items-center justify-center gap-3" data-testid="ws-countdown">
      <Flame size={18} className="text-aviana-gold" /> {text}
    </p>
  );
}

export function YourStay({ img, offering, availability, onChoose }) {
  const rooms = offering?.room_options || [];
  const ext = offering?.extended_stay;
  return (
    <section className="py-24 md:py-32 bg-aviana-paper" data-testid="ws-your-stay">
      <div className="container-aviana">
        <div className="grid lg:grid-cols-2 gap-12 items-end mb-16">
          <div>
            <Overline>Your Stay</Overline>
            <h2 className="font-display text-4xl md:text-6xl text-aviana-ink leading-[1.04]">Four suites. <em className="italic gold-text">One warm home to share.</em></h2>
          </div>
          <p className="text-aviana-inkSoft text-[17px] leading-[1.8] font-serif italic">Our intimate ranch retreat features four beautiful suites, each with a stunning private bathroom, nestled within a welcoming log cabin-style home surrounded by peaceful acreage and horses.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {rooms.map((r) => {
            const open = availability?.[r.key] !== false;
            return (
              <article key={r.key} className="bg-white rounded-[2rem] border border-aviana-border overflow-hidden flex flex-col group" data-testid={`ws-room-${r.key}`}>
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={img(`room-${r.key}`)} alt={r.label} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700" />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-display text-2xl text-aviana-ink">{r.label}</h3>
                  <p className="mt-2 font-display text-3xl text-aviana-plum">${r.price_usd.toLocaleString()}<span className="text-[11px] tracking-[0.18em] uppercase text-aviana-muted ml-2">{r.per_person ? "per person" : "private"}</span></p>
                  <p className="mt-4 text-[14px] text-aviana-muted leading-[1.7] flex-1">{r.blurb}</p>
                  <button type="button" disabled={!open} onClick={() => onChoose(r.key)} data-testid={`ws-choose-${r.key}`}
                    className={`mt-6 ${open ? "btn-primary" : "btn-ghost opacity-60 cursor-not-allowed"} justify-center`}>
                    {open ? <>Claim your place <ArrowRight size={14} /></> : "This suite is full"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
        <div className="mt-6 grid md:grid-cols-5 gap-6">
          <div className="md:col-span-3 rounded-[2rem] overflow-hidden aspect-[16/9] ring-1 ring-aviana-gold/25">
            <img src={img("stay-bathroom")} alt="A suite's private bathroom with a copper soaking tub" loading="lazy" className="w-full h-full object-cover" />
          </div>
          {ext && (
            <div className="md:col-span-2 rounded-[2rem] bg-aviana-plumDeep text-aviana-cream p-8 md:p-10 flex flex-col justify-center" data-testid="ws-extended-stay">
              <Moon size={22} className="text-aviana-goldSoft" />
              <p className="mt-4 text-[11px] tracking-[0.26em] uppercase text-aviana-goldSoft">Optional Third Night · December 20–21</p>
              <p className="mt-2 font-display text-3xl">+${ext.price_usd} <span className="text-[12px] tracking-[0.18em] uppercase text-aviana-cream/70">per person</span></p>
              <p className="mt-4 text-[14px] leading-[1.75] text-aviana-cream/85">{ext.blurb}</p>
            </div>
          )}
        </div>
        <p className="mt-14 text-center font-display text-3xl md:text-4xl text-aviana-ink leading-[1.2] max-w-3xl mx-auto" data-testid="ws-booking-line">{BOOKING_LINE}</p>
        <SolsticeCountdown />
        <p className="mt-5 text-center text-[13px] text-aviana-muted">A $350 deposit holds your place in the circle · Payment plans in up to three installments · Full balance due by January 18, 2027</p>
      </div>
    </section>
  );
}

export function MeetHost({ img }) {
  return (
    <section className="py-24 md:py-32 bg-aviana-cream" data-testid="ws-host">
      <div className="container-aviana max-w-5xl grid md:grid-cols-5 gap-12 items-center">
        <div className="md:col-span-2 rounded-full overflow-hidden aspect-square ring-1 ring-aviana-gold/40 ring-offset-8 ring-offset-aviana-cream">
          <img src={img("host")} alt="Holly Johnson with one of the horses" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="md:col-span-3">
          <Overline>Meet Your Host</Overline>
          <h2 className="font-display text-4xl md:text-5xl text-aviana-ink leading-[1.05]">Holly Johnson</h2>
          <p className="mt-2 text-[12px] tracking-[0.24em] uppercase text-aviana-plum">Your host, guide and the heart of our kitchen</p>
          <div className="mt-7 space-y-5 text-aviana-inkSoft text-[16px] leading-[1.8]">
            <p>Holly is the founder of Aviana and has spent years holding space for women — through aerial yoga, Reiki, sound and ceremony, and retreats where the horses and the land do so much of the healing.</p>
            <p>For this gathering she'll be leading every circle and meditation, and she'll also be in the kitchen, lovingly preparing each nourishing meal you share around the table. Her greatest joy is watching women soften, laugh, rest and remember how good it feels to be cared for.</p>
            <p className="font-serif italic text-aviana-plum text-[18px]">"I can't wait to welcome you inside by the fire, sister."</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Closing({ img }) {
  return (
    <section className="relative py-32 md:py-44 overflow-hidden text-aviana-cream" data-testid="ws-closing">
      <img src={img("closing-bg")} alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-aviana-ink/70" />
      <div className="container-aviana relative max-w-3xl text-center">
        <p className="font-serif italic text-2xl md:text-4xl leading-[1.45]">Come find your winter sanctuary. Let the fire warm you, let the horses ground you, let creativity awaken you and let sisterhood remind you of the beauty of being together.</p>
        <a href="#register" className="btn-gold mt-12 inline-flex !px-10 !py-4" data-testid="ws-closing-register">Register Now <ArrowRight size={14} /></a>
      </div>
    </section>
  );
}
